/**
 * <sunday-m-hero>: the mattress buy hero and its sticky buy bar (sections/sunday-m-hero.liquid).
 *
 * Size: thickness (when the variants have more than one), width tiles, length pills, in/cm, and a
 * custom size cut from the next standard size up. The price, the size line, the EMI promise, the
 * Add button, the three mattress tiles (each at the chosen size) and the bar follow the choice.
 * The chosen size is remembered site-wide (@sunday/state "bed") and written to ?variant=.
 * "Recommended for you" sits on the Help me choose pick (@sunday/choose),
 * on the first mattress before anyone answers. Bubbles (B2B, EMI) open on tap.
 * The bar shows from load, and steps away only while the in-page Add is at least half in view; its size
 * chip opens the size sheet. On the ad landing it shows from load and steps away only while it would cover the in-page Add.
 * Add answers the tap at once (sunday:adding: the button says "Adding…") and the cart opens when it
 * answers (sunday:added). Once this size is in the cart both Adds read "In your cart · Go to cart".
 * Gallery thumbs swap the stage between the render, the product's photos, this mattress's clips, its full film (with sound and controls),
 * then the bare renders and the turning video. Each slide can name its focus (data-pos); bare renders sit whole on their own background (data-bare).
 * Every word comes from the section (cfg.text) or the store; no product is named here.
 */
import { parseAll, uniq, cutFrom, tileName, widthName, CUT } from '@sunday/sizes';
import * as state from '@sunday/state';
import * as cart from '@sunday/cart';
import { paint as paintIn } from '@sunday/incart';
import { current as choice } from '@sunday/choose';
import { esc, money, emi, calm } from '@sunday/util';

const CM = 2.54;
const cm = (n) => Math.round(n * CM);
const fill = (s, v) => String(s || '').replace(/\[(\w+)\]/g, (m, k) => (k in v ? v[k] : m));
const $ = (s, r = document) => r.querySelector(s);
const $$ = (s, r = document) => [...r.querySelectorAll(s)];

class SundayMHero extends HTMLElement {
  connectedCallback() {
    if (this.cfg) return;
    this.cfg = JSON.parse($('script[data-cfg]', this).textContent);
    const c = this.cfg;
    this.t = c.text;
    this.sizes = parseAll(c.variants);
    this.heights = uniq(this.sizes.map((s) => s.h).filter((h) => h != null));
    this.others = Object.fromEntries(Object.entries(c.others || {}).map(([k, v]) => [k, parseAll(v)]));
    this.unit = 'in';
    this.custom = null;
    this.measure = null;

    const start = this.sizes.find((s) => s.id === c.selectedId) || this.sizes[0];
    this.pick = { h: start?.h ?? null, w: start?.w, l: start?.l };
    const bed = state.get('bed');
    const dm = /^(\d+)\s*x\s*(\d+)$/i.exec(c.defaultSize || '');
    if (!c.urlHasVariant && !bed && dm) {
      const d = this.sizes.find((s) => s.w === +dm[1] && s.l === +dm[2]);
      if (d) this.pick = { h: d.h, w: d.w, l: d.l };
    }
    if (!c.urlHasVariant && bed && bed.w && bed.l) {
      const std = this.#std(bed.w, bed.l);
      if (bed.custom && std) this.custom = { w: bed.w, l: bed.l };
      else if (std) this.pick = { h: std.h, w: std.w, l: std.l };
    }
    try {
      if (localStorage.getItem('sunday-unit') === 'cm') this.unit = 'cm';
    } catch (e) {}

    this.$sizes = $('[data-sizes]', this);
    this.$sheetSizes = $('[data-sizes-sheet]', this);
    this.$price = $('[data-price]', this);
    this.$what = $('[data-what]', this);
    this.$add = $('#buy [data-add]', this);
    this.$err = $('[data-err]', this);
    this.$emi = $('[data-emi]', this);
    this.$emiBtn = $('[data-emi-tip]', this);
    this.emiBase = this.$emiBtn ? this.$emiBtn.dataset.tip : '';
    this.$measure = $('[data-measure]', this);
    this.$bar = $('[data-bar]', this);
    this.$sheet = $('[data-size-sheet], [data-sheet]', this);
    this.$chip = $('[data-chip]', this);

    this.addEventListener('click', (e) => this.#click(e));
    this.addEventListener('input', (e) => this.#input(e));
    this.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && e.target.matches('[data-mw],[data-ml]')) this.#useCustom();
    });
    this.#bubbles();
    this.#gallery();
    this.#barWatch();
    this.#pickTag();
    this.render();
  }

  /* ---------- sizes ---------- */
  #at() {
    return this.sizes.filter((s) => this.pick.h == null || s.h === this.pick.h);
  }
  #std(w, l) {
    const exact = this.sizes.find((s) => s.w === w && s.l === l && (this.pick.h == null || s.h === this.pick.h));
    return exact || cutFrom(this.sizes, w, l, this.pick.h);
  }
  get chosen() {
    if (this.custom) return cutFrom(this.sizes, this.custom.w, this.custom.l, this.pick.h);
    return this.#at().find((s) => s.w === this.pick.w && s.l === this.pick.l) || null;
  }
  #in(n) {
    return this.unit === 'cm' ? `${cm(n)} cm` : `${n}″`;
  }
  /** "Queen size 78×60″" (or 198 × 152 cm); for the 2-height mattress, " · 6″" after it. */
  #label(s, short = false) {
    if (!s) return '';
    let name = widthName(s);
    if (short) name = name.replace(/\s+(size|bed)$/i, '');
    const dims = this.unit === 'cm' ? `${cm(s.l)} × ${cm(s.w)} cm` : `${s.l}×${s.w}″`;
    const h = this.heights.length > 1 && s.h ? ` · ${this.#in(s.h)}` : '';
    return `${name} ${dims}${h}`;
  }
  #what() {
    const v = this.chosen;
    if (!v) return '';
    if (this.custom) {
      const dims = this.unit === 'cm' ? `${cm(this.custom.l)} × ${cm(this.custom.w)} cm` : `${this.custom.l}×${this.custom.w}″`;
      return fill(this.t.customLine, { size: dims, from: this.#label(v) });
    }
    return this.#label(v);
  }

  /* The page prints the same rows at first paint (snippets/sunday-m-sizes.liquid): keep the two in step. */
  #sizesHtml(sheet = false) {
    const t = this.t;
    const at = this.#at();
    const out = [];
    if (this.heights.length > 1) {
      const allW = uniq(this.sizes.map((s) => s.w));
      const tag = (h) => {
        const ws = uniq(this.sizes.filter((s) => s.h === h).map((s) => s.w));
        if (ws.length >= allW.length) return '';
        const names = ws.map((w) => tileName(this.sizes.find((s) => s.w === w)));
        return ` <small>${esc(names.join(' & '))}</small>`;
      };
      out.push(`<div class="sd-mh-row"><span class="sd-mh-k">${esc(t.thickness)}</span><div class="sd-mh-hts" role="group" aria-label="${esc(t.thickness)}">${this.heights
        .map((h) => `<button type="button" data-h="${h}" aria-pressed="${this.pick.h === h}">${esc(this.#in(h))}${tag(h)}</button>`)
        .join('')}</div></div>`);
    }
    const widths = uniq(at.map((s) => s.w));
    const custOn = !!(this.custom || this.measure);
    const tile = (w) => {
      const s = at.find((x) => x.w === w);
      const on = !custOn && this.pick.w === w;
      return `<button type="button" class="sd-mh-szt" data-w="${w}" aria-pressed="${on}"><b>${esc(tileName(s))}</b><small>${esc(fill(this.unit === 'cm' ? t.wideCm : t.wideIn, { w: this.unit === 'cm' ? cm(w) : w }))}</small></button>`;
    };
    const cust = sheet
      ? `<button type="button" class="sd-mh-szt" data-custom-sheet aria-pressed="${custOn}"><b>${esc(t.custom)}</b><small>${esc(t.anySize)}</small></button>`
      : `<button type="button" class="sd-mh-szt" data-custom aria-pressed="${custOn}"><b>${esc(t.custom)}</b><small>${this.custom ? esc(this.unit === 'cm' ? `${cm(this.custom.l)}×${cm(this.custom.w)}` : `${this.custom.l}×${this.custom.w}″`) : esc(t.anySize)}</small></button>`;
    out.push(`<div class="sd-mh-row"><span class="sd-mh-k">${esc(t.width)}</span><div class="sd-mh-szw" role="group" aria-label="${esc(t.width)}">${widths.map(tile).join('')}${cust}</div></div>`);
    const lens = uniq(at.filter((s) => s.w === (this.custom ? this.chosen?.w : this.pick.w)).map((s) => s.l));
    if (lens.length > 1) {
      out.push(`<div class="sd-mh-row"><span class="sd-mh-k">${esc(t.length)}</span><div class="sd-mh-szl" role="group" aria-label="${esc(t.length)}">${lens
        .map((l) => {
          const most = l === Number(this.cfg.most) && t.most ? `<span class="sd-mh-most" aria-hidden="true">${esc(t.most)}</span>` : '';
          return `<button type="button" data-l="${l}" aria-pressed="${!custOn && this.pick.l === l}">${esc(this.#in(l))}${most}</button>`;
        })
        .join('')}</div></div>`);
    }
    return out.join('');
  }

  /* The custom size box: your frame, drawn to scale, and the standard size we cut it from. */
  #measured() {
    const m = this.measure || {};
    const w = Number(m.w);
    const l = Number(m.l);
    const ok = w >= CUT.minW && w <= CUT.maxW && l >= CUT.minL && l <= CUT.maxL;
    return { w, l, ok, from: ok ? cutFrom(this.sizes, w, l, this.pick.h) : null };
  }
  #measureHtml() {
    const t = this.t;
    const m = this.measure;
    const isCm = this.unit === 'cm';
    const toU = (v) => (v === '' || v == null ? '' : isCm ? cm(v) : v);
    const { w, l, ok, from } = this.#measured();
    const sc = 2.2;
    const fw = ok ? w : 60;
    const fl = ok ? l : 78;
    const sw = from ? from.w : fw;
    const sl = from ? from.l : fl;
    const W = 200;
    const ox = (W - sw * sc) / 2 + 10;
    const oy = 10;
    const svg = `<svg class="sd-mh-cutsvg" viewBox="0 0 ${W + 20} ${sl * sc + 50}" role="img" aria-label="${esc(t.svgAria)}">
 <defs><pattern id="sd-mh-hat" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)"><path d="M0 0v6" stroke="var(--sd-line)" stroke-width="2"/></pattern></defs>
 <rect x="${ox}" y="${oy}" width="${sw * sc}" height="${sl * sc}" fill="none" stroke="var(--sd-ink3)" stroke-dasharray="5 4"/>
 ${from && (sw > fw || sl > fl) ? `<path d="M${ox + fw * sc} ${oy}h${(sw - fw) * sc}v${sl * sc}h-${(sw - fw) * sc}zM${ox} ${oy + fl * sc}h${fw * sc}v${(sl - fl) * sc}h-${fw * sc}z" fill="url(#sd-mh-hat)" opacity=".8"/>` : ''}
 <rect x="${ox - 6}" y="${oy - 6}" width="${fw * sc + 12}" height="${fl * sc + 12}" rx="4" fill="none" stroke="var(--sd-art-wood-dark)" stroke-width="5"/>
 <rect x="${ox}" y="${oy}" width="${fw * sc}" height="${fl * sc}" rx="3" fill="var(--sd-art-latex)" stroke="var(--sd-c)" stroke-width="3"/>
 <text x="${ox + (fw * sc) / 2}" y="${oy + (fl * sc) / 2}" text-anchor="middle" font-size="13" fill="var(--sd-ink)">${ok ? toU(w) + '×' + toU(l) : '—'}</text>
 <text x="${ox + (fw * sc) / 2}" y="${oy + (fl * sc) / 2 + 16}" text-anchor="middle" font-size="10" fill="var(--sd-ink2)">${esc(ok ? t.yourMattress : t.typeSize)}</text>
 <text x="${ox}" y="${oy + sl * sc + 30}" font-size="10.5" fill="var(--sd-ink2)">${esc(t.dashed)}</text></svg>`;
    const lim = isCm ? `${cm(CUT.maxL)}×${cm(CUT.maxW)} cm` : `${CUT.maxL}×${CUT.maxW}″`;
    const res = from
      ? `<div class="sd-mh-cutres"><b>${esc(fill(t.cutFrom, { from: this.#label(from) }))}</b><span>${esc(t.cutSub)}</span></div>`
      : (m.w || m.l) && !ok
        ? `<p class="sd-warn">${esc(fill(t.tooBig, { max: lim }))}</p>`
        : '';
    const sizeTxt = ok ? `${toU(w)}×${toU(l)}${isCm ? ' cm' : '″'}` : '';
    return `<div class="sd-mh-cut">
 <b class="sd-mh-cuth">${esc(t.insideSize)}</b>
 <div class="sd-mh-cutg"><div data-cutsvg>${svg}</div><div class="sd-mh-cutf">
 <div class="sd-mh-cutin"><label>${esc(t.width)}<input data-mw inputmode="numeric" value="${esc(toU(m.w))}" placeholder="${esc(fill(t.eg, { n: isCm ? 147 : 58 }))}"></label><label>${esc(t.length)}<input data-ml inputmode="numeric" value="${esc(toU(m.l))}" placeholder="${esc(fill(t.eg, { n: isCm ? 193 : 76 }))}"></label></div>
 <p class="sd-mh-cutm">${esc(fill(t.measureLine, { max: lim }))}</p>
 <div data-cutres aria-live="polite">${res}</div>
 <button type="button" class="sd-btn sd-nv sd-full sd-mh-use" data-use${from ? '' : ' disabled'}>${esc(ok ? fill(t.useSize, { size: sizeTxt }) : t.useThis)}</button></div></div></div>`;
  }

  /* ---------- paint ---------- */
  render() {
    const v = this.chosen;
    const t = this.t;
    this.$sizes.innerHTML = this.#sizesHtml(false);
    if (this.$sheetSizes) this.$sheetSizes.innerHTML = this.#sizesHtml(true);
    $$('[data-unit]', this).forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.unit === this.unit)));
    this.$measure.hidden = !this.measure;
    this.$measure.innerHTML = this.measure ? this.#measureHtml() : '';
    const price = v ? money(v.price) : '';
    this.$price.textContent = price;
    this.$what.textContent = this.#what();
    this.$add.disabled = !v;
    this.$add.textContent = v ? `${t.add} · ${price}` : t.add;
    if (this.$emi && v && this.cfg.emiMonths) this.$emi.textContent = fill(t.perkEmi, { amount: money(emi(v.price, this.cfg.emiMonths)) });
    if (this.$emiBtn && v && this.cfg.emiMonths && t.emiNow) {
      const months = this.cfg.emiMonths;
      this.$emiBtn.dataset.tip = `${this.emiBase} ${fill(t.emiNow, { size: this.#label(v, true), a3: money(emi(v.price, 3)), a6: money(emi(v.price, months)), months })}`.trim();
    }
    const bp = $('[data-bar-price]', this);
    if (bp) bp.textContent = price;
    // In your cart · Go to cart once this size is in the cart (@sunday/incart).
    $$('[data-add]', this).forEach((b) => {
      b.dataset.sdKind = 'm';
      b.dataset.sdV = v ? v.id : '';
      b.dataset.sdCustom = v && this.custom ? cart.customSizeText(this.custom.w, this.custom.l) : '';
      paintIn(b);
    });
    const cl = $('[data-chip-label]', this);
    if (cl) cl.textContent = this.custom ? this.#what().split(',')[0] : this.#label(v, true);
    this.#tilePrices();
    this.#emit();
  }

  /* Each mattress tile shows its price at the chosen size (its nearest standard size). */
  #tilePrices() {
    const v = this.chosen;
    if (!v) return;
    const w = this.custom ? this.custom.w : v.w;
    const l = this.custom ? this.custom.l : v.l;
    $$('[data-tile-price]', this).forEach((el) => {
      const list = this.others[el.dataset.tilePrice] || [];
      const hs = uniq(list.map((s) => s.h).filter((h) => h != null));
      // This mattress's own tile: the height chosen here. The others: their lowest price at this size
      // (Ortho Memory 4 at 6″, as every other page shows it; AR, 17 Oct).
      const pick = (h) => list.find((x) => x.w === w && x.l === l && (h == null || x.h === h)) || cutFrom(list, w, l, h);
      const own = el.closest('[aria-current="page"]');
      let s;
      if (own && hs.includes(v.h)) s = pick(v.h);
      else if (!hs.length) s = pick(null);
      else s = hs.map(pick).filter(Boolean).sort((a, b) => a.price - b.price)[0];
      if (s) el.textContent = money(s.price);
    });
  }

  #emit() {
    const v = this.chosen;
    const detail = { ready: !!v, price: v ? money(v.price) : '', label: this.#what(), custom: v && this.custom ? { ...this.custom } : null, w: v ? v.w : null, wname: v ? tileName(v) : '' };
    window.sundayBuyLast = detail;
    this.dispatchEvent(new CustomEvent('sunday:buy', { bubbles: true, detail }));
  }

  #remember() {
    const v = this.chosen;
    if (!v) return;
    state.set('bed', this.custom ? { w: this.custom.w, l: this.custom.l, custom: true, from: this.cfg.name } : { w: v.w, l: v.l, from: this.cfg.name });
    if (!this.custom) {
      const url = new URL(location.href);
      url.searchParams.set('variant', v.id);
      history.replaceState(history.state, '', url);
    }
  }

  /* ---------- actions ---------- */
  #click(e) {
    const b = e.target.closest('button, a');
    if (!b || !this.contains(b)) return;
    const d = b.dataset;
    if (d.h) {
      this.pick.h = Number(d.h);
      const at = this.#at();
      if (!at.some((s) => s.w === this.pick.w && s.l === this.pick.l)) {
        const near = at.find((s) => s.w === this.pick.w && s.l === Number(this.cfg.most)) || at.find((s) => s.w === this.pick.w) || at.find((s) => s.l === this.pick.l) || at[0];
        this.pick.w = near.w;
        this.pick.l = near.l;
      }
      if (this.custom && !this.chosen) this.custom = null;
      this.#remember();
    } else if (d.w) {
      this.custom = null;
      this.measure = null;
      this.pick.w = Number(d.w);
      const lens = this.#at().filter((s) => s.w === this.pick.w).map((s) => s.l);
      if (!lens.includes(this.pick.l)) this.pick.l = lens.includes(Number(this.cfg.most)) ? Number(this.cfg.most) : Math.max(...lens);
      this.#remember();
    } else if (d.l) {
      this.custom = null;
      this.measure = null;
      this.pick.l = Number(d.l);
      this.#remember();
    } else if ('customSheet' in d) {
      this.#sheetOpen(false);
      this.custom = null;
      this.measure = { w: '', l: '' };
      this.render();
      const box = $('[data-measure]', this);
      if (box) box.scrollIntoView({ block: 'center', behavior: calm() ? 'auto' : 'smooth' });
      $('[data-mw]', this)?.focus({ preventScroll: true });
      return;
    } else if ('custom' in d) {
      this.measure = this.measure ? null : { w: this.custom?.w ?? '', l: this.custom?.l ?? '' };
    } else if (d.unit) {
      this.unit = d.unit;
      try {
        localStorage.setItem('sunday-unit', this.unit);
      } catch (err) {}
    } else if ('use' in d) {
      this.#useCustom();
      return;
    } else if ('add' in d || 'barAdd' in d) {
      this.#add(b);
      return;
    } else if ('chip' in d) {
      this.#sheetOpen(this.$sheet.hidden);
      return;
    } else if ('x' in d) {
      this.#sheetOpen(false);
      this.$chip.focus();
      return;
    } else return;
    const keep = b.closest('[data-sizes-sheet]') ? 'sheet' : null;
    this.render();
    if ('custom' in d && this.measure) $('[data-mw]', this)?.focus();
    if (keep && d.w) $(`[data-sizes-sheet] [data-w="${d.w}"]`, this)?.focus();
    if (keep && d.l) $(`[data-sizes-sheet] [data-l="${d.l}"]`, this)?.focus();
  }

  #input(e) {
    if (!this.measure || !e.target.matches('[data-mw],[data-ml]')) return;
    const raw = e.target.value.replace(/[^\d.]/g, '');
    const n = raw === '' ? '' : Number(raw);
    const inches = n === '' ? '' : this.unit === 'cm' ? Math.round(n / CM) : Math.round(n);
    if (e.target.matches('[data-mw]')) this.measure.w = inches;
    else this.measure.l = inches;
    const tmp = document.createElement('div');
    tmp.innerHTML = this.#measureHtml();
    $('[data-cutsvg]', this.$measure).innerHTML = $('[data-cutsvg]', tmp).innerHTML;
    $('[data-cutres]', this.$measure).innerHTML = $('[data-cutres]', tmp).innerHTML;
    $('[data-use]', this.$measure).replaceWith($('[data-use]', tmp));
  }

  #useCustom() {
    const { w, l, from } = this.#measured();
    if (!from) return;
    const std = this.sizes.find((s) => s.w === w && s.l === l && (this.pick.h == null || s.h === this.pick.h));
    this.measure = null;
    if (std) {
      this.custom = null;
      this.pick = { h: std.h, w, l };
    } else this.custom = { w, l };
    this.#remember();
    this.render();
  }

  async #add(btn) {
    const v = this.chosen;
    if (!v || this.busy) return;
    this.busy = true;
    this.$err.hidden = true;
    this.#sheetOpen(false);
    const item = { btn, name: this.cfg.name, what: this.#what(), price: money(v.price), image: this.cfg.image, tint: this.cfg.tint };
    document.dispatchEvent(new CustomEvent('sunday:adding', { detail: item }));
    try {
      const res = await cart.addFromProductPage({ variantId: v.id, productId: this.cfg.productId, size: { w: v.w, l: v.l }, custom: this.custom });
      const line = res.cart.items.find((x) => x.variant_id === v.id && (x.properties || {})._bed === res.bed) || res.cart.items[0];
      document.dispatchEvent(
        new CustomEvent('sunday:added', {
          detail: { ...item, cart: res.cart, line, replaceable: res.replaceable, protector: this.#protector(v, res.cart, res.bed) },
        })
      );
    } catch (err) {
      this.$err.textContent = err.message || '';
      this.$err.hidden = false;
      document.dispatchEvent(new CustomEvent('sunday:add-failed', { detail: { btn, message: err.message || '' } }));
    } finally {
      this.busy = false;
    }
  }

  #protector(v, c, bed) {
    const p = this.cfg.protector;
    if (!p) return null;
    const s = parseAll(p.variants).find((x) => x.w === v.w && x.l === v.l && x.available);
    if (!s) return null;
    if (c.items.some((x) => x.product_id === p.productId && (x.properties || {})._bed === bed)) return null;
    return { id: s.id, price: money(s.price), bed };
  }

  /* ---------- bubbles ---------- */
  #bubbles() {
    let pop = null;
    const close = () => {
      if (!pop) return;
      pop.remove();
      pop = null;
      $$('[data-tip][aria-expanded="true"]', this).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    };
    const open = (b) => {
      close();
      b.setAttribute('aria-expanded', 'true');
      pop = document.createElement('div');
      pop.className = 'sd-mh-pop';
      pop.setAttribute('role', 'tooltip');
      pop.textContent = b.dataset.tip;
      document.body.appendChild(pop);
      const r = b.getBoundingClientRect();
      const w = Math.min(260, innerWidth - 32);
      pop.style.width = w + 'px';
      const left = Math.max(16, Math.min(r.left + r.width / 2 - w / 2, innerWidth - w - 16));
      pop.style.left = left + 'px';
      pop.style.top = r.bottom + 6 + 'px';
    };
    document.addEventListener(
      'click',
      (e) => {
        const b = e.target.closest && e.target.closest('[data-tip]');
        if (b && this.contains(b)) {
          e.preventDefault();
          if (b.getAttribute('aria-expanded') === 'true') close();
          else open(b);
          return;
        }
        if (!(e.target.closest && e.target.closest('.sd-mh-pop'))) close();
      },
      true
    );
    document.addEventListener('keydown', (e) => e.key === 'Escape' && close());
    document.addEventListener('scroll', close, true);
  }

  /* ---------- gallery: render or a clip on the stage ---------- */
  #gallery() {
    const stage = $('[data-stage]', this);
    const img = $('img', stage);
    const clear = () => {
      $('video', stage)?.remove();
      $('[data-photo]', stage)?.remove();
      stage.removeAttribute('data-bare');
      if (img) img.hidden = false;
    };
    $$('[data-th]', this).forEach((b) =>
      b.addEventListener('click', () => {
        $$('[data-th]', this).forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
        clear();
        const kind = b.dataset.th;
        if (kind === 'photo') {
          const p = document.createElement('img');
          Object.assign(p, { src: b.dataset.full, alt: b.dataset.alt || '', decoding: 'async' });
          if (b.dataset.iw) {
            p.width = Number(b.dataset.iw);
            p.height = Number(b.dataset.ih);
          }
          if (b.dataset.pos) p.style.objectPosition = b.dataset.pos;
          if (b.dataset.bare != null) stage.setAttribute('data-bare', '');
          p.dataset.photo = '';
          p.className = 'sd-mh-photo';
          if (img) img.hidden = true;
          stage.appendChild(p);
        } else if (kind === 'video' || kind === 'film') {
          const film = kind === 'film';
          const v = document.createElement('video');
          Object.assign(v, { muted: !film, loop: !film, playsInline: true, poster: b.dataset.poster || '', src: b.dataset.src, preload: 'metadata' });
          if (!film) v.setAttribute('muted', '');
          v.setAttribute('playsinline', '');
          v.setAttribute('aria-label', b.dataset.alt || b.getAttribute('aria-label') || '');
          if (b.dataset.pos) v.style.objectPosition = b.dataset.pos;
          if (b.dataset.bare != null) stage.setAttribute('data-bare', '');
          if (film) {
            v.controls = true;
            v.className = 'sd-mh-film';
          }
          if (img) img.hidden = true;
          stage.appendChild(v);
          if (film || !calm()) v.play().catch(() => {});
          else v.controls = true;
        }
        b.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      })
    );
  }

  /* ---------- the bar ---------- */
  #barWatch() {
    if (!this.$bar) return;
    document.body.classList.add('sd-mh-hasbar');
    const head = document.querySelector('[data-sunday-header]');
    const setTop = () => this.style.setProperty('--sd-mh-top', (head ? head.offsetHeight : 0) + 'px');
    setTop();
    addEventListener('resize', setTop);
    const on = (show) => {
      this.$bar.classList.toggle('sd-on', show);
      this.$bar.setAttribute('aria-hidden', String(!show));
      $$('button', this.$bar).forEach((b) => (b.tabIndex = show ? 0 : -1));
      document.body.classList.toggle('sd-mh-barshow', show);
      if (!show) this.#sheetOpen(false);
    };
    if (this.cfg.landing) {
      // Ad landing: the bar is there from load; it steps away only while it would sit on the in-page Add.
      let tick = 0;
      const check = () => {
        tick = 0;
        const r = this.$add.getBoundingClientRect();
        const barTop = innerHeight - this.$bar.offsetHeight;
        const covers = r.bottom > barTop && r.top < innerHeight;
        const show = !covers;
        if (show !== this.$bar.classList.contains('sd-on')) on(show);
      };
      const ask = () => {
        if (!tick) tick = requestAnimationFrame(check);
      };
      addEventListener('scroll', ask, { passive: true });
      addEventListener('resize', ask);
      check();
    } else if ('IntersectionObserver' in window) {
      // The observer's first answer comes with the first frame, so the bar never slides in and straight out.
      new IntersectionObserver(([e]) => on(e.intersectionRatio < 0.5), { threshold: [0, 0.5, 1] }).observe(this.$add);
    } else on(true);
    document.addEventListener('click', (e) => {
      if (this.$sheet && !this.$sheet.hidden && e.target.isConnected && (!e.target.closest('[data-bar]') || e.target === this.$sheet)) this.#sheetOpen(false);
    });
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.$sheet && !this.$sheet.hidden) {
        this.#sheetOpen(false);
        this.$chip.focus();
      }
    });
  }
  #sheetOpen(open) {
    if (!this.$sheet) return;
    this.$sheet.hidden = !open;
    this.$chip.setAttribute('aria-expanded', String(open));
  }

  /* ---------- "Recommended for you" ---------- */
  #pickTag() {
    const current = () => {
      const pk = choice();
      return pk ? pk.id : null;
    };
    const paint = (id) => {
      const tiles = $$('[data-tiles] [data-handle]', this);
      const target = id && tiles.some((t) => t.dataset.handle === id) ? id : tiles[0] && tiles[0].dataset.handle;
      tiles.forEach((t) => {
        const rc = $('[data-rc]', t);
        if (rc) rc.hidden = t.dataset.handle !== target;
      });
    };
    paint(current());
    document.addEventListener('sunday:pick', (e) => paint(e.detail ? e.detail.id : null));
    document.addEventListener('sunday:state', (e) => e.detail && e.detail.key === 'choose' && paint(current()));
  }
}

if (!customElements.get('sunday-m-hero')) customElements.define('sunday-m-hero', SundayMHero);
